import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App as AntdApp, Modal, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import {
  organizationsAccessResendInviteCreateMutation,
  organizationsOrganizationsRetrieveOptions
} from 'src/api/main'
import { buildWorkspaceInviteUrl } from 'src/features/workspaces/utils/inviteUrl'
import { useShowApiErrors } from 'src/utils'

import { invalidateWorkspaceUserQueries } from '../hooks'
import type { WorkspaceUser } from '../types'

const { Text } = Typography

type Props = {
  workspaceId: number
  open: boolean
  user: WorkspaceUser | null
  onClose: () => void
}

export const UserResendInviteModal = ({
  workspaceId,
  open,
  user,
  onClose
}: Props) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const showApiErrors = useShowApiErrors()

  const { data: organization } = useQuery({
    ...organizationsOrganizationsRetrieveOptions({ path: { id: workspaceId } }),
    enabled: open && Number.isFinite(workspaceId)
  })

  const { mutateAsync, isPending } = useMutation({
    ...organizationsAccessResendInviteCreateMutation(),
    onSuccess: async () => {
      message.success(t('users.modals.resendInvite.success'))
      if (user) {
        await invalidateWorkspaceUserQueries(qc, {
          workspaceId,
          organizationAccessId: user.id,
          userId: user.user.id
        })
      }
      onClose()
    },
    onError: (e) => showApiErrors(e)
  })

  const canResend = !!organization?.domain_name && !!user?.user.email

  const submit = async () => {
    if (!user || !organization?.domain_name) return
    await mutateAsync({
      path: { id: user.id },
      body: { invite_url: buildWorkspaceInviteUrl(organization.domain_name) }
    })
  }

  return (
    <Modal
      open={open}
      centered
      title={t('users.modals.resendInvite.title')}
      okText={t('users.modals.resendInvite.submit')}
      cancelText={t('users.modals.cancel')}
      okButtonProps={{ loading: isPending, disabled: !canResend }}
      cancelButtonProps={{ disabled: isPending }}
      onOk={submit}
      onCancel={() => {
        if (!isPending) onClose()
      }}
      destroyOnHidden
    >
      <p style={{ marginTop: 0, color: 'var(--ant-color-text-secondary)' }}>
        {t('users.modals.resendInvite.description')}
      </p>
      <p style={{ margin: '4px 0 12px' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('users.modals.resendInvite.recipient')}{' '}
        </Text>
        <Text strong>{user?.user.email ?? '—'}</Text>
      </p>
      {!canResend ? (
        <Alert
          type="warning"
          showIcon
          title={t('users.invite.error.workspaceNotReady')}
        />
      ) : null}
    </Modal>
  )
}
